<template>
  <b-dialog
    :visible="isVisible"
    class="live-payment"
    :class="{paying: orderId}"
    @close="onCloseDialog"
  >
    <template v-if="!orderId">
      <div class="user-info">
        <div class="my-avatar" :style="{backgroundImage: `url(${avatar}@64w_64h_1e_1c_1f.webp)`}" />
        <div>
          <div class="login-info">
            <div class="my-username">{{ nickname }}</div>
            <div :class="`my-auth-type ${authType}`"/>
          </div>
          <div class="my-balance">
            {{$t('payment.balance')}}
            <span class="star-icon small" />
            {{balance.toLocaleString()}}
          </div>
        </div>
      </div>
      <h2> {{$t('payment.title')}}</h2>
      <div class="productions">
        <ul class="product-list" v-expose="rechargeDashboardShowMeta">
          <li
            v-for="product in products" class="item"
            :class="{ active: product.product_code === selectedProduct?.product_code }"
            :key="product.product_code"
            @click="() => selectProduct(product)"
          >
            <div class="star-num">
              <span class="star-icon large" />
              {{product.stars.toLocaleString()}}
            </div>
            <div class="star-price">
              {{ getRealPrice(product.product_code) }}
            </div>
          </li>
        </ul>
        <p class="exchange-rate-desc">{{$t('payment.exchangeRateDesc')}}</p>
      </div>
      <div class="channels">
        <h2>{{$t('payment.methodTitle')}}</h2>
        <div class="channels-list-wrap">
          <ul class="channel-list" v-expose="rechargeDashboardShowMeta"
              :style="{transform: `translateX(${-channelPn * (180 + 12) * channelPs}px)`}">
            <li
              v-for="channel in currentChannels"
              :class="{ active: channel?.pay_channel_name === selectedChannel?.pay_channel_name }"
              :key="channel.pay_channel_name"
              @click="() => selectChannel(channel)"
            >
              <img class="channel-icon"  :src="channel.pay_channel_logo" v-if="channel.pay_channel_logo"/>
              <div class="channel-name">{{channel.pay_channel_name}}</div>
            </li>
          </ul>
        </div>
        <div class="channel-pagination">
          <div v-if="channelPn > 0" class="left" @click="e => channelPn -= 1"></div>
          <div v-if="(channelPn + 1) * channelPs < currentChannels.length" class="right" @click="e => channelPn += 1"></div>
        </div>
      </div>
      <b-button class="start-payment" @click="createOrder" :loading="isOrderCreating" :disabled="isOrderCreating">
        {{$t('payment.next')}}
      </b-button>
      <p class="payment-agreement" @click="onClickAgreement">
        {{$t('payment.agree')}}
        <a :href="panelInfo?.protocol_url" target="_blank">{{$t('payment.agreement')}}</a>
      </p>
    </template>
    <template v-else>
      <h2>{{$t('payment.orderTitle')}}</h2>
      <p class="after-payment-desc">{{$t('payment.orderDesc')}}</p>
      <div class="after-payment" v-expose="rechargeIncompletePopupShowMeta">
        <button class="btn-plain" @click="repay">{{$t('payment.repay')}}</button>
        <button class="btn" @click="getPayed">{{$t('payment.getPaid')}}</button>
      </div>
    </template>
  </b-dialog>
</template>

<script lang="ts" setup>
import { watch, onMounted, ref, Ref, computed } from 'vue';
import { useStore } from 'vuex';
import { BDialog, BButton, useMessage } from '@bilibili-intl/components';
import emitter, { EB_EVENT_NAME } from '@/utils/eventbus';
import { useI18n } from 'vue-i18n';
import { IRechargePanelData, IPayData, IProduct, IOrderStatus, IChannelData, IChannel } from '@/service/payment/types';
import PaymentService from '@/service/payment';
import { roomTracker, RechargeDashboardClickPos, RechargeIncompletePopupClickPos } from '@/track/room';
import { AuthType } from '@/service/user/types';
import { getEnv } from '@/common/env';
import PaymentSDK, {FakeCoinCreateOrderParams, CreateOrderRes } from '@bilibili-intl/payment';

const store = useStore();
const { t: $t } = useI18n();
const lang = computed(() => store.state.global.sLocale);
const isVisible: Ref<boolean> = ref(false);

// 用户信息映射
const nickname = computed(() => store.state.global.userInfo.nickname);
const avatar = computed(() => store.state.global.userInfo.avatar);
const authType = computed(() => AuthType[store.state.global.userInfo.auth_type]);
const balance = computed(() => store.state.room?.balance || 0);


const roomId = computed(() => store.state.room.roomInfo.room_id);

const env = getEnv();

let paymentSdkInst  = new PaymentSDK({
  env: env === 'dev' ? 'uat' : env,
  platform: 'web',
  locale: lang.value
});

onMounted(async () => {
  emitter.on(EB_EVENT_NAME.PAYMENT_DIALOG_OPEN, ({ depositStars }) => {
    orderId.value = '';
    selectProduct(products.value[0] as IProduct);
    if (depositStars > 0) {
      for (let i = 0; i < products.value.length; i++) {
        const product = products.value[i];
        if (product.stars >= depositStars) {
          selectProduct(product as IProduct);
          break;
        }
      }
    }
    selectChannel(currentChannels?.value?.[0] as IChannel);
    isVisible.value = true;
  });
  emitter.on(EB_EVENT_NAME.PAYMENT_DIALOG_CLOSE, () => {
    isVisible.value = false;
  });
});



// 获取panel信息
const panelInfo: Ref<IRechargePanelData | null> = ref(null);
const channelInfo: Ref<IChannelData[]> = ref([]);
onMounted(async () => {
  if (import.meta.env.SSR) {
    return;
  }
  paymentSdkInst = new PaymentSDK({
    env: env === 'dev' ? 'uat' : env,
    platform: 'web',
    locale: lang.value
  });
  try {
    const resp = await PaymentService.getRechargePanel();
    panelInfo.value = resp.data;
    channelInfo.value = await paymentSdkInst.queryProductDetailBy({
      product_codes: (resp.data.products || []).map((p: IProduct) => p.product_code),
      customer_id: resp.data.customer_id
    });

    selectProduct(panelInfo.value?.products[0] as IProduct);
    selectChannel(currentChannels?.value?.[0] as IChannel);
  } catch (e) {
    console.warn(e);
  }
});
const products = computed(() => {
  return panelInfo?.value?.products || [];
});


// 选中商品和选中付费方式
const currentChannels: Ref<IChannel[]> = ref([]);
const selectedProduct: Ref<IProduct | null> = ref(null);
const selectProduct = (product: IProduct) => {
  selectedProduct.value = product;
  currentChannels.value = (channelInfo?.value || []).find(c => c.product_code === product.product_code)?.channels || [];
  const selectChannelIndex = currentChannels.value.findIndex(c => c.pay_channel_name === selectedChannel.value?.pay_channel_name);
  selectedChannel.value = currentChannels.value[selectChannelIndex === -1 ? 0 : selectChannelIndex];
};
const selectedChannel: Ref<IChannel | null> = ref(null);
const selectChannel = (channel: IChannel) => {
  selectedChannel.value = channel;
};
// 获取真实价格
const getRealPrice = (productCode: string) => {
  try {
    const channels = channelInfo.value.find(c => c.product_code === productCode)?.channels || [];
    const target = channels.find(c => c.pay_channel_name === selectedChannel.value?.pay_channel_name);
    return target ? `${target?.currency_symbol} ${target.price}` : '';
  } catch (e) {
    console.warn(e);
    return '';
  } 
};
// channel分页设置
const channelPn: Ref<number> = ref(0);
const channelPs: Ref<number> = ref(4);


// 订单跟踪
const orderId: Ref<string> = ref('');
const isOrderCreating: Ref<boolean> = ref(false);

const createOrder = async () => {
  roomTracker.RechargeDashboardClick(roomId.value, selectedProduct.value.stars, RechargeDashboardClickPos.Next);

  try {
    isOrderCreating.value = true;
    const { product_code = '' } = selectedProduct.value || {};
    const {
      channel_product_id = '',
      pay_channel = '',
      pay_channel_id = '',
      sub_pay_channel_id = 0,
      real_channel = ''
    } = selectedChannel.value || {};
    const resp = await PaymentService.createOrder(channel_product_id, product_code, pay_channel, pay_channel_id, sub_pay_channel_id);
    
    const payData: IPayData = JSON.parse(resp.data.pay_data);
    // camel case转snake case 
    const sdkCreateOrderParams : FakeCoinCreateOrderParams = {
      trans_type: payData.transType,
      coin_fee_type: payData.coinFeeType,
      coin_quantity: payData.coinQuantity,
      country_code: payData.countryCode,
      fail_url: payData.failUrl,
      customer_id: payData.customerId,
      service_type: Number(payData.serviceType),
      order_id: payData.orderId,
      order_create_time: payData.orderCreateTime,
      order_expire: Number(payData.orderExpire),
      fee_type: payData.feeType,
      pay_amount: Number(payData.payAmount),
      original_amount: Number(payData.originalAmount),
      device_type: Number(payData.deviceType),
      product_id: payData.productId,
      show_title: payData.showTitle,
      notify_url: payData.notifyUrl,
      return_url: payData.returnUrl,
      timestamp: Number(payData.timestamp),
      version: payData.version,
      trace_id: payData.traceId,
      uid: Number(payData.uid),
      sign_type: payData.signType,
      sign: payData.sign,
      customer_product_code: payData.customerProductCode,
      price_criterion: Number(payData.priceCriterion),
      real_channel,
      pay_channel,
      pay_channel_id,
      sub_pay_channel_id,
      channel_product_id
    };
    const sdkResp: CreateOrderRes = await paymentSdkInst.createOrder(sdkCreateOrderParams);

    isOrderCreating.value = false;

    orderId.value = resp.data.order_id;
    PaymentService.jumpCashier(sdkResp.pay_channel_url);
    checkOrder(orderId.value, counterThrehold, 0, false);
  } catch (e: any) {
    useMessage({
      text: e.message,
      duration: 3000,
      type: 'error'
    });
  } finally {
    isOrderCreating.value = false;
  }
};

// 重新支付
const repay = () => {
  roomTracker.RechargeIncompletePopupClick(roomId.value, orderId.value, RechargeIncompletePopupClickPos.Repay);

  if (orderChecker) {
    clearTimeout(orderChecker);
    orderChecker = null;
  }
  orderId.value = '';
  selectProduct(panelInfo.value?.products[0] as IProduct);
  selectChannel(currentChannels?.value?.[0] as IChannel);
};

// 用户预期支付成功，仅检查一次，关闭后续检查
const getPayed = () => {
  roomTracker.RechargeIncompletePopupClick(roomId.value, orderId.value, RechargeIncompletePopupClickPos.GetPaid);
  if (isCheckerStarted) {
    return;
  }
  isCheckerStarted = true;
  checkOrder(orderId.value, counterThrehold, 0, true);
};

// 周期检查
let orderChecker: NodeJS.Timeout | null = null;
let isCheckerStarted: boolean = false;
const counterThrehold: number = 30;
const checkOrder = async (
  orderId: string,
  threhold: number = counterThrehold,
  counter: number = 0,
  showMessage: boolean
) => {
  if (!orderId) {
    return;
  }
  if (counter === 0) {
    orderChecker && clearTimeout(orderChecker);
    orderChecker = null;
  }
  try {
    const resp = await PaymentService.queryOrder(orderId);
    isCheckerStarted = false;
    switch (resp.data.status) {
      case IOrderStatus.SUCCESS: {
        isVisible.value = false;
        emitter.emit(EB_EVENT_NAME.PAYMENT_DIALOG_SUCCESS_PAYED);
        return;
      }
      case IOrderStatus.CANCEL: {
        useMessage({
          text: $t('payment.toast_cancel'),
          duration: 3000,
          type: 'error'
        });
        return;
      }
      case IOrderStatus.TIMEOUT_CLOSE: {
        useMessage({
          text: $t('payment.toast_timeout'),
          duration: 3000,
          type: 'error'
        });
        return;
      }
      case IOrderStatus.FAIL: {
        useMessage({
          text: $t('payment.toast_fail'),
          duration: 3000,
          type: 'error'
        });
        emitter.emit(EB_EVENT_NAME.PAYMENT_DIALOG_FAILED_PAYED);
        return;
      }
      case IOrderStatus.PROCESSING: {
        if (counter + 1 >= threhold) {
          return;
        }
        if (showMessage && counter === 0) {
          useMessage({
            text: $t('payment.toast_notPay'),
            duration: 3000,
            type: 'error'
          });
        }
        orderChecker = setTimeout(() => {
          checkOrder(orderId, threhold, counter + 1, false);
        }, 6000);
        return;
      }
    }
  } catch {}
};

watch(isVisible, (newValue) => {
  if (!newValue && orderChecker) {
    clearTimeout(orderChecker);
    orderChecker = null;
  }
});

// 埋点
const rechargeDashboardShowMeta = roomTracker.getRechargeDashboardShowMeta(roomId.value);

const rechargeIncompletePopupShowMeta = computed(
  () => roomTracker.getRechargeIncompletePopupShowMeta(roomId.value, orderId.value)
);
const onClickAgreement = () => {
  roomTracker.RechargeDashboardClick(roomId.value, selectedProduct.value.stars, RechargeDashboardClickPos.Agreement);
};

// 关闭弹窗，清除数据
const onCloseDialog = () => {
  isVisible.value = false;
  selectProduct(panelInfo.value?.products[0] as IProduct);
  selectChannel(currentChannels?.value?.[0] as IChannel);
  if(orderId.value){
    roomTracker.RechargeIncompletePopupClick(roomId.value, orderId.value, RechargeIncompletePopupClickPos.Close);
  }
};
</script>

<style lang="less" scoped>
@import "@/styles/polyfill/gap.less";

h2 {
  font-size: 18px;
  color: #333333;
  line-height: 22px;
  font-weight: 700;
  margin: 18px 0 14px;
}
.user-info {
  display: flex;
  flex-direction: row;
  padding-bottom: 20px;
  width: 90%;
  overflow: hidden;

  & > div {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .my-avatar {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    align-self: center;
    background-image: url(/src/client/assets/noface.png);
    background-position: center;
    background-size: contain;
    position: relative;
    margin-right: 16px;
    flex-shrink: 0;
  }

  .login-info {
    display: flex;
    flex-direction: row;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    .my-username {
      margin: 8px 0;
      line-height: 18px;
      height: 18px;
      color: #333333;   
      font-size: 16px;
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex-shrink: 1;
      flex-grow: 0;
    }

    .my-auth-type {
      width: 16px;
      height: 16px;
      align-self: center;
      margin-left: 2px;
      flex-shrink: 0;
      flex-grow: 0;
      background-position: center;
      background-size: 16px;
      background-repeat: no-repeat;
      overflow: hidden;
      display: none;

      &.apple {
        display: inline-block;
        background-image: url('@/assets/login/login_type_apple.png');
      }
      &.google {
        display: inline-block;
        background-image: url('@/assets/login/login_type_google.png');
      }
      &.facebook {
        display: inline-block;
        background-image: url('@/assets/login/login_type_facebook.png');
      }
      &.twitter {
        display: inline-block;
        background-image: url('@/assets/login/login_type_twitter.png');
      }
      &.phone {
        display: inline-block;
        background-image: url('@/assets/login/login_type_phone.png');
      }
      &.email {
        display: inline-block;
        background-image: url('@/assets/login/login_type_email.png');
      }
    }
  }

  .my-balance {
    opacity: 0.9;
    font-size: 14px;
    color: #333;
    line-height: 18px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;

    .star-icon {
      margin: 0 2px 0 6px;
    }
  }
}



.star-icon {
  display: inline-block;
  background: url('@/assets/star-big.png');
  background-position: center;
  background-size: contain;

  &.small {
    width: 16px;
    height: 16px;
  }

  &.large {
    width: 20px;
    height: 20px;
  }
}

  
.productions {
  max-height: 320px;

  .exchange-rate-desc {
    font-size: 12px;
    color: #999999;
    line-height: 14px;
    padding: 12px 0 8px;
  }
}

ul {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;

  & > li {
    flex: 1 1 180px;
    padding: 12px;
    cursor: pointer;
    border: 2px solid rgba(153, 153, 153, 0.2);
    border-radius: 6px;
    height: 80px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-grow: 0;
    flex-shrink: 0;

    &:nth-child(4n+2), &:nth-child(4n+3), &:nth-child(4n+4) {
      margin-left: 12px;
    }
   
    &.active {
      background: rgba(255, 225, 131, 0.15);
      border-color: rgba(255, 212, 42, 1);
    }
  }
}

ul.product-list {
  & > li {
    height: 80px;

    &:nth-child(n+5) {
      margin-top: 12px;
    }

    .star-num {
      font-size: 20px;
      color: #333;
      line-height: 28px;
      font-weight: 700;
      display: flex;
      align-items: center;
      
      
      .star-icon  {
        margin-right: 4px;
      }
    }

    .star-price {
      font-size: 14px;
      color: #999;
      line-height: 18px;
      margin-top: 6px;
    }
  }
}




.channels {
  position: relative;

  .channels-list-wrap {
    overflow: hidden;
  }
  ul.channel-list {
    flex-wrap: nowrap;
    position: relative;
    transition: transform 0.3s ease-in-out;

    & > li {
      height: 54px;
      flex-direction: row;
      justify-content: flex-start;

      &:nth-child(4n+5){
        margin-left: 12px;
      }

      .channel-icon {
        width: 28px;
        height: 28px;
      }
      .channel-name {
        margin-left: 8px;
        font-size: 14px;
        color: #333333;
        line-height: 18px;
        font-weight: 700;
      }
    }
  }
  .channel-pagination {
    position: absolute;
    top: 48px;
    left: 0;
    right: 0;
    overflow: hidden;
    pointer-events: none;
    height: 30px;


    & > div {
      top: 0;
      width: 30px;
      height: 30px;
      background-repeat: no-repeat;
      background-size: 20px;
      background-position: center 3px;
      background-image: url('@/assets/arrow-white.svg');
      background-color: rgba(68, 68 , 68, 0.5);
      cursor: pointer;
      transform-origin: center;
      pointer-events: auto;
      display: inline-block;
      position: absolute;
    }

    .left {
      left: 0;
      transform: translate(-6px, 0) rotate(90deg);
      border-radius: 6px 6px 0 0;
    }

    .right {
      right: 0;
      transform: translate(6px, 0) rotate(-90deg);
      border-radius: 6px 6px 0 0;
    }
  }
}


.btn {
  border-radius: 4px;
  font-size: 16px;
  color: #333;
  text-align: center;
  line-height: 44px;
  font-weight: 700;
  cursor: pointer;
  background: #ffd42a;
  
  flex-grow: 1;

  &:hover {
    background: #ecbb39;
  }
}

.btn-plain {
  .btn();
  display: block;
  border: 2px solid rgba(255, 212, 42, 1);
  background: #fff;

  &:hover {
    background: rgba(255, 225, 131, 0.15);
  }
}

.start-payment {
  .btn();

  display: flex;
  justify-content: center;
  align-items: center;
  width: 372px;
  margin: 24px auto 20px;
}

.payment-agreement {
  font-size: 14px;
  color: #999;
  line-height: 16px;
  text-align: center;

  & > a {
    color: #4c93ff;
  }
}

.after-payment {
  display: flex;
  flex-direction: row;
  .flex-gap(16px);
}

.after-payment-desc {
  font-size: 14px;
  color: #999;
  line-height: 18px;
  margin-bottom: 30px;
  word-break: break-word;
}
</style>
<style lang="less">
.dialog.live-payment {
  &.paying {
    .dialog__container {
      width: 510px;
      min-height: 0;
      height: auto;
    }
  }

  .dialog__container {
    width: 820px;
    height: 620px;
    max-height: 90%;


    .dialog__header {
      padding: 0;
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      z-index: 2;
    }

    .dialog__body {
      padding: 20px 32px 32px;
    }
  }
}
</style>